<template>
  <Modal
    :show="show"
    title="编辑站点图标"
    size="large"
    @close="handleClose"
  >
    <div class="space-y-3">
      <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_176px] lg:items-start">
          <div class="min-w-0">
            <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
              站点图标会固定裁剪为 1:1，并统一导出为 128x128 PNG。
            </p>

            <div class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950">
              <div class="relative h-[320px] w-full overflow-hidden bg-grid-pattern sm:h-[340px] lg:h-[360px] xl:h-[380px]">
                <img
                  ref="imageRef"
                  :src="sourceImageUrl"
                  alt="待裁剪 Favicon"
                  class="block max-h-none max-w-none"
                />
              </div>
            </div>

            <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
              建议使用简洁图形或单字母标识，避免细节过多导致缩小后难以辨认。
            </p>
          </div>

          <div class="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-950/60">
            <div class="space-y-4">
              <div>
                <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">缩放</p>
                <div class="grid grid-cols-1 gap-2">
                  <button type="button" :class="[toolButtonClass, 'w-full justify-center']" :disabled="!cropperReady" @click="zoomImage(0.1)">放大</button>
                  <button type="button" :class="[toolButtonClass, 'w-full justify-center']" :disabled="!cropperReady" @click="zoomImage(-0.1)">缩小</button>
                </div>
              </div>

              <div>
                <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">变换</p>
                <div class="grid grid-cols-1 gap-2">
                  <button type="button" :class="[toolButtonClass, 'w-full justify-center']" :disabled="!cropperReady" @click="rotateImage(-90)">左转</button>
                  <button type="button" :class="[toolButtonClass, 'w-full justify-center']" :disabled="!cropperReady" @click="rotateImage(90)">右转</button>
                </div>
              </div>

              <div>
                <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">操作</p>
                <button type="button" :class="[toolButtonClass, 'w-full justify-center']" :disabled="!cropperReady" @click="resetCrop">重置</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-3 dark:border-gray-700">
      <button
        type="button"
        :disabled="confirming"
        class="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        @click="handleClose"
      >
        取消
      </button>
      <button
        type="button"
        :disabled="confirming || !cropperReady"
        class="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        @click="handleConfirm"
      >
        {{ confirming ? '上传中...' : cropperReady ? '确认并上传' : '正在初始化...' }}
      </button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import Modal from '@/components/common/Modal.vue'

const OUTPUT_SIZE = 128

interface Props {
  show: boolean
  sourceImageUrl: string
  originalFileName: string
  confirming?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  confirming: false,
})

const emit = defineEmits<{
  close: []
  confirm: [file: File]
}>()

const imageRef = ref<HTMLImageElement | null>(null)
const cropperRef = ref<Cropper | null>(null)
const cropperReady = ref(false)

const toolButtonClass = computed(() =>
  cropperReady.value
    ? 'inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800'
    : 'inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 opacity-50 cursor-not-allowed dark:border-gray-600 dark:text-gray-200'
)

/**
 * 销毁现有 Cropper 实例。
 */
const destroyCropper = () => {
  cropperRef.value?.destroy()
  cropperRef.value = null
  cropperReady.value = false
}

/**
 * 移除 Cropper 初始化后残留的原始图片节点。
 */
const removeSourceImageElement = () => {
  const sourceImageElement = imageRef.value
  if (!sourceImageElement?.parentNode) {
    return
  }

  sourceImageElement.parentNode.removeChild(sourceImageElement)
}

/**
 * 初始化 Cropper。
 */
const initCropper = async () => {
  if (!props.show || !props.sourceImageUrl) {
    return
  }

  await nextTick()

  if (!imageRef.value) {
    return
  }

  destroyCropper()

  const image = imageRef.value
  if (!image.complete) {
    await new Promise<void>((resolve) => {
      image.onload = () => resolve()
      image.onerror = () => resolve()
    })
  }

  cropperRef.value = new Cropper(imageRef.value, {
    aspectRatio: 1,
    viewMode: 1,
    dragMode: 'move',
    autoCropArea: 1,
    background: false,
    responsive: true,
    checkOrientation: false,
    guides: true,
    movable: true,
    zoomable: true,
    scalable: false,
    rotatable: true,
    ready: () => {
      cropperReady.value = true
      removeSourceImageElement()
    },
  })
}

/**
 * 缩放图片。
 */
const zoomImage = (delta: number) => {
  if (!cropperReady.value) return
  cropperRef.value?.zoom(delta)
}

/**
 * 旋转图片。
 */
const rotateImage = (degree: number) => {
  if (!cropperReady.value) return
  cropperRef.value?.rotate(degree)
}

/**
 * 重置裁剪状态。
 */
const resetCrop = () => {
  if (!cropperReady.value || !cropperRef.value) return
  cropperRef.value.reset()
  cropperRef.value.setAspectRatio(1)
}

/**
 * 关闭弹窗。
 */
const handleClose = () => {
  if (props.confirming) {
    return
  }
  emit('close')
}

/**
 * 生成裁剪后的文件并确认上传。
 */
const handleConfirm = async () => {
  if (!cropperRef.value) {
    return
  }

  const canvas = cropperRef.value.getCroppedCanvas({
    width: OUTPUT_SIZE,
    height: OUTPUT_SIZE,
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high',
    fillColor: 'rgba(255,255,255,0)',
  })

  if (!canvas) {
    return
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png')
  })

  if (!blob) {
    return
  }

  const baseName = props.originalFileName.includes('.')
    ? props.originalFileName.slice(0, props.originalFileName.lastIndexOf('.'))
    : props.originalFileName || 'tenant-favicon'

  const exportFile = new File(
    [blob],
    `${baseName}-favicon.png`,
    { type: 'image/png' },
  )

  emit('confirm', exportFile)
}

watch(
  () => [props.show, props.sourceImageUrl] as const,
  ([show, sourceImageUrl]) => {
    if (!show || !sourceImageUrl) {
      destroyCropper()
      return
    }

    void initCropper()
  },
)

onBeforeUnmount(() => {
  destroyCropper()
})
</script>

<style scoped>
.bg-grid-pattern {
  background-color: #f8fafc;
  background-image:
    linear-gradient(to right, rgba(148, 163, 184, 0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(148, 163, 184, 0.12) 1px, transparent 1px);
  background-size: 20px 20px;
}

:global(.cropper-bg) {
  background-image:
    linear-gradient(45deg, rgba(148, 163, 184, 0.08) 25%, transparent 25%, transparent 75%, rgba(148, 163, 184, 0.08) 75%, rgba(148, 163, 184, 0.08)),
    linear-gradient(45deg, rgba(148, 163, 184, 0.08) 25%, transparent 25%, transparent 75%, rgba(148, 163, 184, 0.08) 75%, rgba(148, 163, 184, 0.08));
  background-position: 0 0, 10px 10px;
  background-size: 20px 20px;
}
</style>
